import { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { FormLayout } from "@/components/Forms";
import FormField from "@/components/Forms/FormField";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";

type FormDataT = {
  name: string;
  max_images_count: number;
};

interface Props {
  onClose: () => void;
}

const NewSearchModal: FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const methods = useForm<FormDataT>({
    defaultValues: {
      max_images_count: 20,
    },
  });
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { id } = await api.post<never, { id: number }>(
        "/v1/instagram-images-searches",
        data
      );

      if (id) {
        await queryClient.invalidateQueries(["instagram_searches_list"], {
          exact: false,
        });

        navigate(`/general/instagram-images-serp/${id}`);
      }
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="New Instagram Search" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="name"
                type="text"
                label="Keyword"
                placeholder="Add keyword"
              />
              <FormField<FormDataT>
                control={control}
                name="max_images_count"
                type="text"
                placeholder="Enter count"
                label="Max images count (1 - 100)"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {isSubmitting ? "Searching" : "Search"}
            </Button>
            <Button disabled={isSubmitting} color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default NewSearchModal;
